import '@app/App.css';
import { Badge } from '@comps/ui/badge';
import { Label } from '@comps/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@comps/ui/select';
import { Switch } from '@comps/ui/switch';
import { isCompozzError } from '@dto/CompozzError';
import { Relation } from '@dto/records';
import User, { EditRightAccess, hasGlobalRightAccess, MembersRightType, Role, UpdateUserRole } from '@dto/Users';
import { GRAY_300_COLOR } from '@utils/colors';
import { BASE_URL } from '@utils/constants';
import { fetchRoles } from '@utils/model';
import { toastError } from '@utils/toasts';
import { TW_Members, TW_MembersSearch, TW_MembersSearchContainer, TW_MembersTable, TW_MembersTitle, TW_MembersTitleContainer, TW_NoRolesDefined, TW_OwnerBadge, TW_OwnerBadgeSmall, TW_RoleSettingsButton, TW_RoleSettingsButtonIcon, TW_RoleSettingsButtonText, TW_UserRoleSelectTrigger, TW_UsersBodyRows, TW_UsersNameCell, TW_UsersNameCellFirstLastNames, TW_UsersNameCellUsername, TW_WorkspaceMembersOnlyButton, TW_WorkspaceMembersOnlyButtonSwitch, TW_WorkspaceMembersOnlyButtonText } from '@utils/twClasses';
import { idToKey } from '@utils/types';
import { get, put } from '@utils/utils';
import { Setting2 } from 'iconsax-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { translate } from 'src/i18n/i18n';
import { useAppStore, useModelStore, useUserStore } from 'src/store';


function Members() {

  const navigate = useNavigate();
  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const [roles, setRoles] = useState<Role[]>([]);
  const wsColor = useModelStore(state => state.wsColor);
  const wsLightColor = useModelStore(state => state.wsLightColor);
  const isDarkColor = useModelStore(state => state.wsIsDarkColor);
  const users = useAppStore(state => state.usersByKeys);
  const updateUsers = useAppStore(state => state.updateUsers);
  const user = useUserStore(state => state.user);
  const [activeUsers, setActiveUsers] = useState<User[]>([]);
  const [displayAllUsers, setDisplayAllUsers] = useState(false);
  const [search, setSearch] = useState("");
  const [usersRoles, setUsersRoles] = useState<Map<string, string>>(new Map());
  const userRole = useModelStore(state => state.userRole);

  useEffect(() => {
    setActiveUsers(Array.from(users.values()).filter(user => user.active));
  }, [users]);

  useEffect(() => {
    fetchRoles(workspace?._key, null, null, setRoles);
    fetchUsersRoles();
  }, [workspace]);

  function fetchUsersRoles() {
    if (!workspace?._key) {
      console.error("Workspace key is not set");
      return;
    }

    get<any>(`${BASE_URL}/admin/workspaces/${workspace._key}/roleByUsers`).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          toastError(response.message);
        } else {
          const usersRoles = new Map<string, string>(Object.entries(response));
          setUsersRoles(usersRoles);
        }
      }
    });
  }

  const hasMemberRight = hasGlobalRightAccess(user, userRole, MembersRightType, EditRightAccess);

  if (!user || !hasMemberRight) {
    console.error("User is not allowed to access this page (user undefined or not owner)");
    navigate("/");
  }

  function NameCell(user: User) {
    return <div className={TW_UsersNameCell + " " + (user.active ? "" : "opacity-50")}>
      <span className={TW_UsersNameCellFirstLastNames}>{user.firstname} {user.lastname}</span>
      <span className={TW_UsersNameCellUsername}>{user.username}</span>
    </div>;
  }

  function getOnRoleSelected(userKey: string) {
    return (roleKey: string) => {
      if (roleKey === "role_none") {
        roleKey = "";
      }
      if (!workspace?._key) {
        console.error("Workspace key is not set");
        return;
      }
      const updateUserRole: UpdateUserRole = {
        WorkspaceKey: workspace?._key || "",
        UserKey: userKey,
        RoleKey: roleKey
      };
      put<Relation>(`${BASE_URL}/admin/workspaces/${workspace?._key}/users/role`, updateUserRole).then(response => {
        if (response) {
          if (isCompozzError(response)) {
            if (response.i18n) {
              toastError(translate(t, response.i18n, response.params));
            } else {
              toastError(response.message);
            }
          } else {
            const link = response as Relation;
            const newUsersRoles = new Map(usersRoles);
            newUsersRoles.set(userKey, idToKey(link._to));
            setUsersRoles(newUsersRoles);
          }
        } else if (roleKey === "") {
          const newUsersRoles = new Map(usersRoles);
          newUsersRoles.delete(userKey);
          setUsersRoles(newUsersRoles);
        }
      });
    }
  }

  function RoleCell(user: User) {
    if (!user.owner) {
      if (roles.length === 0) {
        return <p className={TW_NoRolesDefined}>{t('users.table.body.no.roles.defined')}</p>;
      } else {
        const roleDisabled = !user.active;
        var roleKey = usersRoles.get(user._key);
        if (!roleKey) {
          roleKey = "role_none";
        }
        return (
          <Select value={roleKey} onValueChange={getOnRoleSelected(user._key)} disabled={roleDisabled}>
          <SelectTrigger className={TW_UserRoleSelectTrigger}>
            <SelectValue placeholder={t('roles.select.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem key={"role_none"} value={"role_none"}>{t('roles.select.none')}</SelectItem>
            {roles.map(role => {
              return (
                <SelectItem key={role._key} value={role._key}>{role.name}</SelectItem>
              )
            })}
          </SelectContent>
          </Select>
        )
      }
    } else {
      return <>
      <Badge className={TW_OwnerBadge} style={{ backgroundColor: wsColor, color: isDarkColor ? 'white' : 'black',}}>{t('users.table.body.owner')}</Badge>
      <Badge className={TW_OwnerBadgeSmall} style={{ backgroundColor: wsColor, color: isDarkColor ? 'white' : 'black',}}>{t('users.table.body.owner.small')}</Badge>
    </>
    }
  }

  function filterUsers(usersToFilter: User[]) {
    const filteredUsers = usersToFilter.filter(user => 
      user.firstname?.toLowerCase()?.includes(search.toLowerCase()) || 
      user.lastname?.toLowerCase()?.includes(search.toLowerCase()) || 
      user.username?.toLowerCase()?.includes(search.toLowerCase())
    );
    if (!displayAllUsers) {
      return filteredUsers.filter(user => user.owner || usersRoles.get(user._key));
    } else {
      return filteredUsers;
    }
  }


  if (!workspace?._key) {
    console.error("Workspace key is not set");
    return;
  }

  const style = {
    scrollbarColor: wsColor + " " + wsLightColor
  }

  const filteredUsers = filterUsers(activeUsers);
  return (
    <section className={TW_Members} style={style}>
      <div className={TW_MembersTitleContainer}>
        <h1 className={TW_MembersTitle}>{t('members.title')}</h1>
        <div className={TW_RoleSettingsButton}>
          <Setting2 className={TW_RoleSettingsButtonIcon} />
          <p className={TW_RoleSettingsButtonText} onClick={() => navigate(`/workspaces/${workspace._key}/roles`)}>{t('members.role.settings')}</p>
        </div>
      </div>
      <div className={TW_MembersSearchContainer}>
        <input type="text" placeholder={t('members.search')} className={TW_MembersSearch} value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className={TW_WorkspaceMembersOnlyButton}>
          <Label className={TW_WorkspaceMembersOnlyButtonText}>{t('members.workspace.display.all.users')}</Label>
          <Switch className={TW_WorkspaceMembersOnlyButtonSwitch} checked={displayAllUsers} onCheckedChange={setDisplayAllUsers} style={{ backgroundColor: (displayAllUsers ? wsColor : GRAY_300_COLOR) }} />
        </div>
      </div>
      <table className={TW_MembersTable}>
        <thead>
          <tr>
            <th className="w-3/5">{t('members.table.header.name')}</th>
            <th className="w-2/5">{t('members.table.header.owner')}</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((user) => {
            return (
              <tr key={user._key} className={TW_UsersBodyRows}>
                <td>{NameCell(user)}</td>
                <td className="text-right flex flex-row justify-end items-center h-16">{RoleCell(user)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </section>
  );
};

export default Members;